import { supabase } from '../supabaseClient';
import type {
  ClientPortalScope,
  ClientPortalSettings,
  GuardContactVisibility,
  ReportReleaseMode,
  ServiceRequestStatus,
} from './clientPortalService';

export interface ClientPortalMemberRow {
  id: string;
  email: string;
  full_name: string | null;
  job_title: string | null;
  role: 'primary' | 'member';
  status: 'active' | 'disabled';
  permissions: ClientPortalScope[];
  site_scope: string[] | null;
  last_login_at: string | null;
  created_at: string;
}

export interface AgencyServiceRequest {
  id: string;
  client_contract_id: string;
  site_id: string | null;
  request_type: string;
  urgency: 'routine' | 'urgent' | 'emergency';
  status: ServiceRequestStatus;
  title: string;
  details: string | null;
  requested_start_at: string | null;
  requested_end_at: string | null;
  guards_required: number;
  response_note: string | null;
  handled_at: string | null;
  created_at: string;
  client_contracts?: { name: string | null } | null;
  sites?: { name: string | null } | null;
}

export interface PostOrderRow {
  id: string;
  company_id: string;
  site_id: string;
  post_id: string | null;
  category: string;
  title: string;
  body: string;
  display_order: number;
  is_client_visible: boolean;
  version: number;
  effective_from: string | null;
  created_at: string;
  updated_at: string;
}

export interface SavePostOrderParams {
  id?: string;
  siteId: string;
  category: string;
  title: string;
  body: string;
  displayOrder?: number;
  isClientVisible: boolean;
  effectiveFrom?: string | null;
}

/**
 * The patch shape mirrors the settings columns one-for-one so the form can send
 * only what the admin actually changed.
 */
export type ClientPortalSettingsPatch = Partial<{
  live_tracking_enabled: boolean;
  active_guards_enabled: boolean;
  patrol_progress_enabled: boolean;
  incident_reports_enabled: boolean;
  daily_reports_enabled: boolean;
  patrol_records_enabled: boolean;
  visitor_logs_enabled: boolean;
  exports_enabled: boolean;
  post_orders_enabled: boolean;
  schedule_enabled: boolean;
  service_requests_enabled: boolean;
  sub_accounts_enabled: boolean;
  incident_release_mode: ReportReleaseMode;
  daily_report_release_mode: ReportReleaseMode;
  guard_contact_visibility: GuardContactVisibility;
  report_history_days: number | null;
}>;

export const clientPortalAdminService = {
  async fetchSettings(clientContractId: string): Promise<ClientPortalSettings | null> {
    const { data, error } = await supabase
      .from('client_portal_settings')
      .select('*')
      .eq('client_contract_id', clientContractId)
      .maybeSingle();
    if (error) throw error;
    return (data as ClientPortalSettings | null) ?? null;
  },

  async updateSettings(
    clientContractId: string,
    patch: ClientPortalSettingsPatch,
  ): Promise<ClientPortalSettings> {
    const { data, error } = await supabase.rpc('client_portal_update_settings', {
      p_client_contract_id: clientContractId,
      p_patch: patch,
    });
    if (error) throw error;
    return data as ClientPortalSettings;
  },

  async fetchMembers(clientContractId: string): Promise<ClientPortalMemberRow[]> {
    const { data, error } = await supabase.rpc('client_portal_admin_members', {
      p_client_contract_id: clientContractId,
    });
    if (error) throw error;
    return ((data ?? []) as ClientPortalMemberRow[]).map((row) => ({
      ...row,
      permissions: Array.isArray(row.permissions) ? row.permissions : [],
    }));
  },

  /** Approve (or retract) a report held back by the 'on_approval' rule. */
  async setReportClientRelease(reportId: string, released: boolean): Promise<string | null> {
    const { data, error } = await supabase.rpc('set_report_client_release', {
      p_report_id: reportId,
      p_released: released,
    });
    if (error) throw error;
    return (data as { client_released_at: string | null }).client_released_at;
  },

  async fetchServiceRequests(options: { openOnly?: boolean } = {}): Promise<AgencyServiceRequest[]> {
    let query = supabase
      .from('client_service_requests')
      .select(`
        id, client_contract_id, site_id, request_type, urgency, status, title, details,
        requested_start_at, requested_end_at, guards_required, response_note, handled_at, created_at,
        client_contracts:client_contracts(name),
        sites:sites(name)
      `)
      .order('created_at', { ascending: false });

    if (options.openOnly) {
      query = query.in('status', ['submitted', 'acknowledged']);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as unknown as AgencyServiceRequest[];
  },

  async respondToServiceRequest(
    requestId: string,
    status: 'acknowledged' | 'scheduled' | 'declined' | 'completed',
    responseNote?: string | null,
  ): Promise<void> {
    const { error } = await supabase.rpc('client_service_request_respond', {
      p_request_id: requestId,
      p_status: status,
      p_response_note: responseNote ?? null,
    });
    if (error) throw error;
  },

  async fetchPostOrders(siteId: string): Promise<PostOrderRow[]> {
    const { data, error } = await supabase
      .from('site_post_orders')
      .select('*')
      .eq('site_id', siteId)
      .order('display_order', { ascending: true })
      .order('title', { ascending: true });
    if (error) throw error;
    return (data ?? []) as PostOrderRow[];
  },

  async savePostOrder(params: SavePostOrderParams): Promise<PostOrderRow> {
    if (params.id) {
      const { data, error } = await supabase
        .from('site_post_orders')
        .update({
          category: params.category,
          title: params.title,
          body: params.body,
          display_order: params.displayOrder ?? 0,
          is_client_visible: params.isClientVisible,
          effective_from: params.effectiveFrom ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', params.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as PostOrderRow;
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', (await supabase.auth.getUser()).data.user?.id ?? '')
      .single();
    if (profileError) throw profileError;

    const { data, error } = await supabase
      .from('site_post_orders')
      .insert({
        company_id: (profile as { company_id: string }).company_id,
        site_id: params.siteId,
        category: params.category,
        title: params.title,
        body: params.body,
        display_order: params.displayOrder ?? 0,
        is_client_visible: params.isClientVisible,
        effective_from: params.effectiveFrom ?? null,
      })
      .select('*')
      .single();
    if (error) throw error;
    return data as PostOrderRow;
  },

  async deletePostOrder(postOrderId: string): Promise<void> {
    const { error } = await supabase
      .from('site_post_orders')
      .delete()
      .eq('id', postOrderId);
    if (error) throw error;
  },
};
